import React, { useState } from 'react';

const BalanceReductionQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [formData, setFormData] = useState({
    action_type: 'action_added',
    description: '',
    user_name: currentUser?.email || 'Sistema'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      alert('Por favor ingresa una descripción de la acción');
      return;
    }

    setLoading(true);
    try {
      await onSave(record.id, formData);
      onClose();
    } catch (error) {
      console.error('Error al guardar acción rápida:', error);
      alert('Error al guardar la acción. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const actionTypes = [
    { value: 'action_added', label: '⚡ Acción Agregada', color: 'text-orange-600' },
    { value: 'observation_added', label: '💬 Observación Agregada', color: 'text-indigo-600' },
    { value: 'status_changed', label: '🔄 Estado Cambiado', color: 'text-purple-600' },
    { value: 'follow_up', label: '📞 Seguimiento Realizado', color: 'text-blue-600' },
    { value: 'issue_resolved', label: '✅ Problema Resuelto', color: 'text-green-600' },
    { value: 'escalated', label: '⬆️ Escalado', color: 'text-red-600' }
  ];

  const quickActions = [
    {
      type: 'follow_up',
      title: 'Seguimiento Realizado',
      description: 'Se realizó seguimiento telefónico con el cliente para revisar el estado del presupuesto'
    },
    {
      type: 'observation_added',
      title: 'Cliente Reporta Problema',
      description: 'Cliente indica que el presupuesto sigue bajando sin explicación aparente'
    },
    {
      type: 'action_added',
      title: 'Revisión de Cuenta',
      description: 'Se revisó la configuración de la cuenta publicitaria y se identificaron posibles optimizaciones'
    },
    {
      type: 'issue_resolved',
      title: 'Problema Resuelto',
      description: 'Se solucionó el problema de reducción de presupuesto mediante ajustes en la campaña'
    }
  ];

  const handleQuickAction = (action) => {
    setFormData({
      ...formData,
      action_type: action.type,
      description: action.description
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Acción Rápida
              </h2>
              <p className="text-green-100 text-sm mt-1">
                Cliente: {record.nombre_cliente} • Campaña: {record.campana}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Quick Actions */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Acciones Rápidas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action)}
                  className="p-3 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all duration-200 text-left"
                >
                  <div className="font-medium text-gray-800 text-sm mb-1">
                    {action.title}
                  </div>
                  <div className="text-xs text-gray-600 line-clamp-2">
                    {action.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Action Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Acción
              </label>
              <select
                value={formData.action_type}
                onChange={(e) => setFormData({ ...formData, action_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                {actionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción de la Acción
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                placeholder="Describe la acción realizada o la observación..."
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                {formData.description.length}/500 caracteres
              </div>
            </div>

            {/* User Info */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Registrado por:</span> {formData.user_name}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {new Date().toLocaleString('es-ES')}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !formData.description.trim()}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Guardando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Guardar Acción
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BalanceReductionQuickActionModal;
